import { useId } from 'react';
import { NavLink } from 'react-router';

import { cx } from '@/lib/cx';
import { Dica } from '@/ui/primitives/tooltip';

import { AlternarMenu } from './alternar-menu';
import { Marca } from './marca';
import { type GrupoNavegacao } from './tipos';

interface BarraLateralProps {
  nomeProduto: string;
  grupos: readonly GrupoNavegacao[];
  recolhida: boolean;
  /** Sem ele não há controle de recolher (caso da gaveta no celular, que tem o próprio fechar). */
  aoAlternar?: () => void;
}

export function BarraLateral({ nomeProduto, grupos, recolhida, aoAlternar }: BarraLateralProps) {
  const idLista = useId();
  return (
    <nav
      aria-label="Principal"
      className={cx(
        'flex h-full flex-col gap-6 overflow-hidden border-r border-borda bg-superficie px-3 py-4',
        'transition-[width] duration-[var(--duracao-media)] ease-saida motion-reduce:transition-none',
        recolhida ? 'w-barra-recolhida' : 'w-barra',
      )}
    >
      <div
        className={cx(
          'flex px-1.5',
          recolhida ? 'flex-col items-center gap-2' : 'items-center justify-between gap-2',
        )}
      >
        <Marca nome={nomeProduto} compacta={recolhida} className="min-w-0" />
        {aoAlternar && (
          <AlternarMenu recolhida={recolhida} aoAlternar={aoAlternar} controla={idLista} />
        )}
      </div>
      <div
        id={idLista}
        className="grid flex-1 content-start gap-5 overflow-y-auto overflow-x-hidden"
      >
        {grupos.map((grupo) => (
          <div key={grupo.titulo} className="grid gap-0.5">
            <p
              className={cx(
                'truncate px-2.5 pb-1 text-xs text-texto-fraco',
                recolhida && 'sr-only',
              )}
            >
              {grupo.titulo}
            </p>
            {grupo.itens.map((item) => (
              <ItemLateral key={item.para} item={item} recolhida={recolhida} />
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
}

function ItemLateral({
  item,
  recolhida,
}: {
  item: GrupoNavegacao['itens'][number];
  recolhida: boolean;
}) {
  const link = (
    <NavLink
      to={item.para}
      end={item.para === '/'}
      className={({ isActive }) =>
        cx(
          'relative flex h-9 items-center gap-2.5 rounded-controle px-2.5 text-base transition-colors duration-[var(--duracao-rapida)] [&_svg]:size-4.5 [&_svg]:shrink-0',
          recolhida && 'justify-center px-0',
          isActive
            ? 'bg-marca-suave font-medium text-marca before:absolute before:-left-3 before:h-5 before:w-1 before:rounded-r-pilula before:bg-marca'
            : 'text-texto-suave hover:bg-superficie-hover hover:text-texto',
        )
      }
      aria-label={recolhida ? item.rotulo : undefined}
    >
      {item.icone}
      {!recolhida && <span className="min-w-0 truncate">{item.rotulo}</span>}
      {item.contador ? <Contador n={item.contador} recolhida={recolhida} /> : null}
    </NavLink>
  );
  return recolhida ? <Dica texto={item.rotulo}>{link}</Dica> : link;
}

/** Bolinha com o número; menu recolhido mostra só o ponto (o número fica no rótulo acessível). */
function Contador({ n, recolhida }: { n: number; recolhida: boolean }) {
  const texto = n > 99 ? '99+' : String(n);
  return recolhida ? (
    <span
      aria-label={`${String(n)} pendentes`}
      className="absolute right-1.5 top-1.5 size-2 animate-surgir rounded-pilula bg-perigo"
    />
  ) : (
    <span
      aria-label={`${String(n)} pendentes`}
      className="ml-auto animate-surgir rounded-pilula bg-perigo-suave px-1.5 text-xs font-semibold text-perigo tabular"
    >
      {texto}
    </span>
  );
}
